/**
 * The html-design agent's animation skill registry — a static library of vetted motion techniques
 * the agent loads on demand via load_skill (progressive disclosure: the system prompt carries one
 * name+description line per skill; the full instructions enter context only when relevant).
 *
 * Deliberately independent of the Toolbox skill store (js/src/agent/skills/skillStore.ts) and of
 * codeo's registry: the techniques here are rewritten to the HtmlAsset composition contract (one
 * paused GSAP timeline, --l5-* tokens), which is a different contract from codeo's.
 * Shares only the Skill shape.
 */
import { LoadHtmlDesignSkillTool } from '../LoadHtmlDesignSkillTool';

import { HTML_DESIGN_ANIMATION_SKILLS } from './definitions';
import { HTML_DESIGN_SKILL_CATEGORIES, HTML_DESIGN_SKILL_META } from './skillMeta';

import type { Skill } from 'js/src/agent/skills/types';

export function getHtmlDesignSkills(): Skill[] {
    return HTML_DESIGN_ANIMATION_SKILLS;
}

export function findHtmlDesignSkill(name: string): Skill | undefined {
    return HTML_DESIGN_ANIMATION_SKILLS.find((skill) => skill.name === name);
}

/**
 * The "## Animation skills" system-prompt section: the per-skill index lines plus the instruction to
 * load relevant skills before authoring motion.
 */
export function getSkillsSystemPromptSection(): string {
    const skills = getHtmlDesignSkills();
    if (skills.length === 0) {
        return '';
    }

    const formatLine = (skill: Skill): string => {
        const tags = HTML_DESIGN_SKILL_META[skill.name]?.tags ?? [];
        const tagSuffix = tags.length > 0 ? ` _(${tags.join(', ')})_` : '';
        return `- ${skill.name}: ${skill.description}${tagSuffix}`;
    };

    // Group skills under their category headers, in the category order declared in skillMeta. Any
    // skill missing metadata (a registry test guards against this) still appears, under a trailing
    // "Other" group, so it never vanishes from the index.
    const seen = new Set<string>();
    const groups = HTML_DESIGN_SKILL_CATEGORIES.map(({ id, label }) => {
        const inGroup = skills.filter(
            (skill) => HTML_DESIGN_SKILL_META[skill.name]?.category === id,
        );
        inGroup.forEach((skill) => seen.add(skill.name));
        if (inGroup.length === 0) {
            return '';
        }
        return `**${label}**\n${inGroup.map(formatLine).join('\n')}`;
    }).filter(Boolean);

    const ungrouped = skills.filter((skill) => !seen.has(skill.name));
    if (ungrouped.length > 0) {
        groups.push(`**Other**\n${ungrouped.map(formatLine).join('\n')}`);
    }

    return `
## Animation skills

A library of vetted motion techniques, grouped by what a moment is *about*. Each skill is a working implementation already rewritten to the composition contract above (one paused timeline seeked by progress, --l5-* tokens with fallbacks, GSAP-only motion) plus the craft details that make the technique read as premium. The italic keywords after each are content cues — match them to the moment you're building (a stat → Numbers & data, a scene change → Camera & transitions).

${groups.join('\n\n')}

Before you author or edit motion, decide which techniques the graphic calls for (scan the groups and keywords above for what the moment is about) and call \`${LoadHtmlDesignSkillTool.toolName}\` with each relevant skill's exact name to read its implementation. Do this as your first authoring action — before writing the composition, not after lint complains. Adapt the loaded snippet's selectors, times, and values to your graphic; don't reinvent a technique a skill already implements, and don't force a loaded skill in where it doesn't serve the moment.

Pick the whole set in one go and load them in a single turn.

Skills are for motion you are adding. When the request is a change to an existing composition that introduces no new technique — copy, numbers, colors, sizes, timing values — skip \`${LoadHtmlDesignSkillTool.toolName}\` entirely and edit directly.`;
}
